import * as React from 'react'

import { cn } from '@/utils'

interface CardProps extends React.ComponentPropsWithoutRef<'div'> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('rounded-lg border bg-white text-blue-7', className)}
      {...props}
    />
  ),
)
Card.displayName = 'Card'

interface CardHeaderProps extends React.ComponentPropsWithoutRef<'div'> {}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex flex-col space-y-1.5 p-6', className)}
      {...props}
    />
  ),
)
CardHeader.displayName = 'CardHeader'

interface CardTitleProps extends React.ComponentPropsWithoutRef<'h3'> {}

const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  ),
)
CardTitle.displayName = 'CardTitle'

interface CardDescriptionProps extends React.ComponentPropsWithoutRef<'p'> {}

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  CardDescriptionProps
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-blue-5', className)} {...props} />
))
CardDescription.displayName = 'CardDescription'

interface CardContentProps extends React.ComponentPropsWithoutRef<'div'> {}

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
  ),
)
CardContent.displayName = 'CardContent'

interface CardFooterProps extends React.ComponentPropsWithoutRef<'div'> {}

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center p-6 pt-0', className)}
      {...props}
    />
  ),
)
CardFooter.displayName = 'CardFooter'

interface CardHeadingProps {
  title: string
  description?: React.ReactNode
  actions?: React.ReactNode
  className?: string
}

// The one card header: sentence-case title, optional one-line description, actions on the right.
const CardHeading = ({
  title,
  description,
  actions,
  className,
}: CardHeadingProps) => (
  <CardHeader
    className={cn(
      'flex-row items-start justify-between gap-4 space-y-0 pb-4',
      className,
    )}
  >
    <div className="min-w-0 space-y-1.5">
      <CardTitle>{title}</CardTitle>
      {description ? (
        <CardDescription className="text-ink-subtle">
          {description}
        </CardDescription>
      ) : null}
    </div>
    {actions ? (
      <div className="flex h-4 shrink-0 items-center gap-2">{actions}</div>
    ) : null}
  </CardHeader>
)

export {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardHeading,
  CardTitle,
}
